import React, {forwardRef} from "react";

interface IDataTableV2Config {
  tableConfig?: {
    stickyHeader?: boolean;
  };
  cachingFilters?: {
    enableCaching: boolean;
    // Persist a single module/table slice. `moduleName` is the table uniqueKey,
    // `configValue` is that table's bare state.
    saveCachingIntoServer?: (moduleName: string, configValue: any) => void;
    getCachingConfigFromServerAtLoad?: () => any;
  };
}

interface IDataTableV2Context extends IDataTableV2Config {
  cachingDataState: IDataTableV2CachedDataState;
  getCachedDataByKey: (uniqueKey: string) => any | null;
  saveDataInCache: (uniqueKey: string, updatedData: any) => void;
  // False until the saved config has been resolved (from server on login, or
  // localStorage on full reload). Tables must not persist until this is true,
  // so a slow server response can't be clobbered by default mount-time state.
  isCacheReady: boolean;
}

const DataTableContext = React.createContext<IDataTableV2Context>({
  tableConfig: {},
  cachingDataState: {},
  getCachedDataByKey: () => {},
  saveDataInCache: () => {},
  isCacheReady: true,
});

export interface IDataTableV2CachedDataState {
  [key: string]: any;
}

export interface DataTableV2ProviderHandle {
  getCachingConfigFromServerAtLoad?: () => any;
}

export const useDataTableV2Context = () => React.useContext(DataTableContext);

export const DataTableV2Provider=forwardRef<
DataTableV2ProviderHandle,
{ children?: React.ReactNode } & IDataTableV2Config
>((props, ref) => {
  const {
    cachingFilters = { enableCaching: false, ...props.cachingFilters },
    tableConfig,
  } = props;
  const { enableCaching } = cachingFilters;

  const [cachingDataState, setCachingDataState] =
    React.useState<IDataTableV2CachedDataState>({});
  // When caching is disabled there's nothing to wait for → ready immediately.
  const [isCacheReady, setIsCacheReady] = React.useState<boolean>(
    !enableCaching,
  );

  const getCachedDataByKey = (uniqueKey: string): any | null => {
    if (cachingDataState[uniqueKey]) {
      return cachingDataState[uniqueKey];
    }
    return null;
  };

  const saveDataInCache = (uniqueKey: string, updatedData: any) => {
    if (!enableCaching) return;

    // Update the central store (single source of truth), keyed by module.
    const cachedData = localStorage.getItem("tablePreferences");
    const parsedData = cachedData ? JSON.parse(cachedData) : {};
    const finalData = { ...parsedData, [uniqueKey]: updatedData };

    setCachingDataState(finalData);
    localStorage.setItem("tablePreferences", JSON.stringify(finalData));

    // Persist ONLY this module's slice to the server (per-module upsert).
    if (cachingFilters.saveCachingIntoServer) {
      cachingFilters.saveCachingIntoServer(uniqueKey, updatedData);
    }
  };

  React.useEffect(() => {
    (async () => {
      if (enableCaching) {
        const cachedData = localStorage.getItem("tablePreferences");
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          setCachingDataState(parsedData);
          // localStorage had the config synchronously (full reload path) →
          // it's safe to consider cache ready even before the server responds.
          setIsCacheReady(true);
        }
      }
    })();

    // Failsafe: never keep tables from persisting indefinitely if the server
    // load is never triggered (e.g. ref timing). Give the server load a window,
    // then unblock.
    const t = setTimeout(() => setIsCacheReady(true), 8000);
    return () => clearTimeout(t);
  }, []);

  React.useImperativeHandle(ref, () => ({
    getCachingConfigFromServerAtLoad: async () => {
      if (cachingFilters.getCachingConfigFromServerAtLoad) {
        try {
          // Server returns a store already keyed by module:
          //   { EVENT_LEADS: {...}, CHAT_LEADS: {...} }
          const serverData =
            (await cachingFilters.getCachingConfigFromServerAtLoad()) || {};
          // Merge over any localStorage seed; server wins per module.
          const cachedData = localStorage.getItem("tablePreferences");
          const localData = cachedData ? JSON.parse(cachedData) : {};
          const merged = { ...localData, ...serverData };
          localStorage.setItem("tablePreferences", JSON.stringify(merged));
          setCachingDataState(merged);
        } catch (e) {
          // ignore; fall through to mark ready so tables aren't blocked forever
        } finally {
          setIsCacheReady(true);
        }
      } else {
        setIsCacheReady(true);
      }
    },
  }));

  return (
    <DataTableContext.Provider
      value={{
        cachingDataState: cachingDataState,
        tableConfig: tableConfig,
        getCachedDataByKey: getCachedDataByKey,
        saveDataInCache,
        isCacheReady,
      }}
    >
      {props.children}
    </DataTableContext.Provider>
  );
});
